import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMembershipBenefitsEnabled } from "../components/PreLaunchGuard";
import { requestMembership } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, Ship, Building, UtensilsCrossed, Globe, Loader2, ArrowRight } from "lucide-react";

const benefits = [
  { icon: Ship, text: "Réductions jusqu'à -15% sur les croisières" },
  { icon: Building, text: "Tarifs préférentiels dans les hôtels partenaires" },
  { icon: UtensilsCrossed, text: "Bons plans restaurants exclusifs" },
  { icon: Globe, text: "Conseils personnalisés pour vos voyages" },
];

export default function JoinPage() {
  const { isAuthenticated, user, isMember } = useAuth();
  const benefitsEnabled = useMembershipBenefitsEnabled();
  const navigate = useNavigate();
  const [step, setStep] = useState(isAuthenticated ? 2 : 1);
  const [loading, setLoading] = useState(false);
  
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });

  const [membershipForm, setMembershipForm] = useState({
    address: "",
    city: "",
    postal_code: "",
    country: "France",
    motivation: ""
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    // Registration would happen here, then move to step 2
    // For now, redirect to registration page
    navigate("/inscription");
  };

  const handleMembershipRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await requestMembership(membershipForm);
      toast.success("Demande d'adhésion envoyée ! Nous vous contacterons bientôt.");
      navigate("/espace-membre");
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la demande";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // If already a member, redirect
  if (isMember) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <div className="text-center">
          <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">Vous êtes déjà adhérent !</h1>
          <p className="text-muted-foreground mb-6">Accédez à votre espace membre pour voir vos avantages.</p>
          <Link to="/espace-membre">
            <Button className="bg-accent hover:bg-accent/90">
              Espace membre
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="join-page">
      {/* Hero */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <p className="font-accent text-accent tracking-wide mb-4">Adhésion — 39€/an</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Rejoignez ITERCATUS
            </h1>
            <p className="text-white/80 text-lg">
              {benefitsEnabled
                ? "En devenant adhérent, vous accédez à des avantages exclusifs et soutenez notre mission de partage et de découverte."
                : "En devenant adhérent, vous soutenez une rédaction indépendante et un projet éditorial sur le voyage — reportages, tests, carnets, fiches pratiques."}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Benefits — remplacés par un message institutionnel sobre
              quand `membership_benefits_enabled=false` (promesses commerciales
              inactives masquées). Architecture avantages intacte pour
              réactivation future. */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-primary mb-6">
              {benefitsEnabled ? "Avantages de l'adhésion" : "Soutenir ITERCATUS"}
            </h2>
            {benefitsEnabled ? (
              <div className="space-y-4 mb-8">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white p-4 border border-border">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center shrink-0">
                      <benefit.icon className="h-5 w-5 text-accent" />
                    </div>
                    <p className="text-primary pt-2">{benefit.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 bg-white p-4 border border-border">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center shrink-0">
                    <Globe className="h-5 w-5 text-accent" />
                  </div>
                  <p className="text-primary pt-2">
                    Soutenir une rédaction indépendante et un journal de voyage sans publicité intrusive
                  </p>
                </div>
                <div className="flex items-start gap-4 bg-white p-4 border border-border">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center shrink-0">
                    <Check className="h-5 w-5 text-accent" />
                  </div>
                  <p className="text-primary pt-2">
                    Participer au projet associatif ITERCATUS (loi 1901) : reportages, tests, carnets, fiches pratiques
                  </p>
                </div>
                <p className="text-sm text-muted-foreground italic px-1">
                  L'association tissera au fil du temps des partenariats avec des acteurs du voyage ; les éventuels avantages ne seront proposés que lorsqu'ils existeront réellement et seront clairement annoncés.
                </p>
              </div>
            )}

            <div className="bg-accent/10 border border-accent/20 p-6 rounded-lg">
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                Comment ça marche ?
              </h3>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span>Créez votre compte ou connectez-vous</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span>Remplissez le formulaire d'adhésion</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span>Votre demande est validée par notre équipe</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-accent text-white rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span>{benefitsEnabled ? "Profitez immédiatement de tous les avantages !" : "Votre carte d'adhérent est émise, vous soutenez le projet éditorial."}</span>
                </li>
              </ol>
            </div>
          </div>

          {/* Form */}
          <div>
            {!isAuthenticated ? (
              <div className="bg-white p-8 border border-border">
                <h2 className="font-heading text-xl font-bold text-primary mb-6">
                  Étape 1 : Créez votre compte
                </h2>
                <p className="text-muted-foreground mb-6">
                  Vous avez déjà un compte ?{" "}
                  <Link to="/connexion" className="text-accent hover:underline">
                    Connectez-vous
                  </Link>
                </p>
                <Link to="/inscription">
                  <Button className="w-full bg-accent hover:bg-accent/90">
                    Créer mon compte
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-white p-8 border border-border">
                <div className="flex items-center gap-2 mb-6">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-green-600 font-medium">Connecté en tant que {user?.name}</span>
                </div>
                
                <h2 className="font-heading text-xl font-bold text-primary mb-6">
                  Formulaire d'adhésion
                </h2>
                
                <form onSubmit={handleMembershipRequest} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Adresse</label>
                    <Input
                      value={membershipForm.address}
                      onChange={(e) => setMembershipForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="123 rue du Voyage"
                      className="h-12"
                      data-testid="address-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Code postal</label>
                      <Input
                        value={membershipForm.postal_code}
                        onChange={(e) => setMembershipForm(prev => ({ ...prev, postal_code: e.target.value }))}
                        placeholder="75001"
                        className="h-12"
                        data-testid="postal-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-2">Ville</label>
                      <Input
                        value={membershipForm.city}
                        onChange={(e) => setMembershipForm(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Paris"
                        className="h-12"
                        data-testid="city-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Pourquoi souhaitez-vous nous rejoindre ? (optionnel)
                    </label>
                    <Textarea
                      value={membershipForm.motivation}
                      onChange={(e) => setMembershipForm(prev => ({ ...prev, motivation: e.target.value }))}
                      placeholder="Parlez-nous de votre passion pour le voyage..."
                      rows={4}
                      className="resize-none"
                      data-testid="motivation-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-accent hover:bg-accent/90 h-12"
                    disabled={loading}
                    data-testid="submit-membership-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer ma demande
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Votre demande sera examinée par notre équipe dans les plus brefs délais.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
